// PlatformBlocks — tenant-agnostic editorial block library for the
// BlockRenderer recipe shape. Tailwind + Lilach-style chrome
// (rounded-2xl white cards, hairline + tiny shadow, stone/amber palette,
// RTL-friendly logical properties).
//
// Each exported component is one block type registered in
// architectureConfig. Components receive their props verbatim from the
// page_content row.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  CircleHelp,
  ExternalLink,
  FileText,
  ListChecks,
  Sparkles,
} from "lucide-react";

// ── Inline rich-text helper ────────────────────────────────────────────────
// Lightweight subset: **bold**, *italic*, `code`, [label](href).
function renderRich(text?: string): ReactNode {
  if (!text) return null;
  const parts: ReactNode[] = [];
  const re = /(\[[^\]]+\]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("[")) {
      const link = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      if (link) {
        const [, label, href] = link;
        const isExternal = /^https?:\/\//.test(href);
        if (isExternal) {
          parts.push(
            <a
              key={key++}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-amber-700 underline-offset-2 hover:underline"
            >
              {label}
            </a>,
          );
        } else {
          parts.push(
            <Link
              key={key++}
              to={href}
              className="font-medium text-amber-700 underline-offset-2 hover:underline"
            >
              {label}
            </Link>,
          );
        }
      }
    } else if (tok.startsWith("`")) {
      parts.push(
        <code
          key={key++}
          className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[0.85em] text-stone-700"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("**")) {
      parts.push(
        <strong key={key++} className="font-semibold text-stone-900">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("*")) {
      parts.push(
        <em key={key++} className="italic">
          {tok.slice(1, -1)}
        </em>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ── Tone tokens — shared across Callout / Chip / BulletList ────────────────
type Tone = "default" | "info" | "success" | "warn" | "error" | "muted";

const TONE: Record<Tone, { surface: string; border: string; ink: string }> = {
  default: { surface: "bg-white",        border: "border-stone-200/60", ink: "text-stone-800"   },
  info:    { surface: "bg-blue-50/60",   border: "border-blue-200/60",  ink: "text-blue-800"    },
  success: { surface: "bg-emerald-50/60",border: "border-emerald-200/60",ink: "text-emerald-800"},
  warn:    { surface: "bg-amber-50/60",  border: "border-amber-200/60", ink: "text-amber-800"   },
  error:   { surface: "bg-rose-50/60",   border: "border-rose-200/60",  ink: "text-rose-800"    },
  muted:   { surface: "bg-stone-50/80",  border: "border-stone-200/70", ink: "text-stone-700"   },
};

// ──────────────────────────────────────────────────────────────────────────
// Hero — page header (eyebrow → title → optional lede/subtitle).
// ──────────────────────────────────────────────────────────────────────────
export function Hero({
  title,
  subtitle,
  eyebrow,
  lede,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  lede?: string;
}) {
  return (
    <header className="mb-7">
      {eyebrow && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
          {eyebrow}
        </p>
      )}
      <h1 className="mt-2 text-xl font-semibold leading-[1.25] tracking-tight text-stone-900 sm:text-2xl">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-3 max-w-2xl text-[15px] leading-[1.65] text-stone-600">
          {renderRich(subtitle)}
        </p>
      )}
      {lede && (
        <p className="mt-3 max-w-2xl text-[15px] leading-[1.7] text-stone-600">
          {renderRich(lede)}
        </p>
      )}
    </header>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Section — heading wrapper. Children render inside the card.
// `body` renders as a paragraph BEFORE children when provided.
// ──────────────────────────────────────────────────────────────────────────
export function Section({
  heading,
  body,
  tone = "default",
  children,
}: {
  heading?: string;
  body?: string;
  tone?: Tone;
  children?: ReactNode;
}) {
  const t = TONE[tone];
  return (
    <section
      className={`mb-6 rounded-2xl border ${t.border} ${t.surface} p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)] sm:p-7`}
    >
      {heading && (
        <header className="mb-4 border-b border-stone-100 pb-3">
          <h2 className="text-base font-semibold leading-[1.35] tracking-tight text-stone-900 sm:text-lg">
            {heading}
          </h2>
        </header>
      )}
      {body && (
        <div className={`mb-4 text-[15px] leading-[1.85] ${t.ink} last:mb-0`}>
          {renderRich(body)}
        </div>
      )}
      {children}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// BodyText — paragraph(s) with markdown-lite. Paragraphs split on \n\n.
// ──────────────────────────────────────────────────────────────────────────
export function BodyText({ body }: { body: string }) {
  const paragraphs = (body || "").split(/\n{2,}/).filter(Boolean);
  return (
    <div className="space-y-3 text-[15px] leading-[1.85] text-stone-700">
      {paragraphs.map((p, i) => (
        <p key={i} className={p.includes("\n") ? "whitespace-pre-wrap" : ""}>
          {renderRich(p)}
        </p>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Callout — single info/decision/debate box.
// ──────────────────────────────────────────────────────────────────────────
export function Callout({
  tone = "info",
  title,
  body,
  icon,
}: {
  tone?: Tone;
  title?: string;
  body?: string;
  icon?: "info" | "decision" | "question" | "spark";
}) {
  const t = TONE[tone];
  const Icon =
    icon === "decision"
      ? ArrowRight
      : icon === "question"
        ? CircleHelp
        : icon === "spark"
          ? Sparkles
          : FileText;
  return (
    <div
      className={`mb-3 rounded-xl border ${t.border} ${t.surface} p-4 last:mb-0`}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${t.ink}`} />
        <div className="min-w-0 flex-1">
          {title && (
            <div className={`text-sm font-semibold ${t.ink}`}>{title}</div>
          )}
          {body && (
            <div
              className={`${title ? "mt-1" : ""} text-[14px] leading-[1.65] ${t.ink}/85`}
            >
              {renderRich(body)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Table — headers + rows, RTL-friendly.
// ──────────────────────────────────────────────────────────────────────────
export function Table({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | number | null)[][];
}) {
  return (
    <div className="my-3 overflow-x-auto rounded-xl border border-stone-200/70">
      <table className="w-full border-collapse text-[14px]">
        <thead>
          <tr className="bg-stone-50/80 text-stone-600">
            {(headers || []).map((h, i) => (
              <th
                key={i}
                className="px-3 py-2 text-start text-[11px] font-semibold uppercase tracking-[0.08em]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row, ri) => (
            <tr
              key={ri}
              className={ri < rows.length - 1 ? "border-b border-stone-100" : ""}
            >
              {(row || []).map((cell, ci) => (
                <td
                  key={ci}
                  className="px-3 py-2 align-top leading-[1.55] text-stone-800"
                >
                  {cell == null ? "" : renderRich(String(cell))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// BulletList — variants: plain / check / action / question.
// `items` is string[] for plain/question, or {text, done?} for check/action.
// ──────────────────────────────────────────────────────────────────────────
type BulletItem = string | { text: string; done?: boolean; status?: "todo" | "done" | "in_progress" };

export function BulletList({
  items,
  variant = "plain",
}: {
  items: BulletItem[];
  variant?: "plain" | "check" | "action" | "question";
}) {
  const list = items || [];
  return (
    <ul className="my-2 space-y-1.5 text-[15px] leading-[1.65] text-stone-700">
      {list.map((it, i) => {
        const text = typeof it === "string" ? it : it.text;
        const done =
          typeof it === "object"
            ? Boolean(it.done) || it.status === "done"
            : false;
        const Icon =
          variant === "check"
            ? done
              ? CheckCircle2
              : Circle
            : variant === "action"
              ? ListChecks
              : variant === "question"
                ? CircleHelp
                : null;
        const iconTint =
          variant === "check"
            ? done
              ? "text-emerald-500"
              : "text-stone-400"
            : variant === "action"
              ? "text-amber-600"
              : variant === "question"
                ? "text-blue-500"
                : "text-stone-400";
        return (
          <li key={i} className="flex items-start gap-2.5">
            {Icon ? (
              <Icon className={`mt-1 h-4 w-4 shrink-0 ${iconTint}`} />
            ) : (
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-400" />
            )}
            <span
              className={
                done
                  ? "flex-1 text-stone-400 line-through"
                  : "flex-1"
              }
            >
              {renderRich(text)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ImageBlock — centered figure with optional caption.
// ──────────────────────────────────────────────────────────────────────────
export function ImageBlock({
  src,
  alt,
  caption,
  maxWidth,
}: {
  src: string;
  alt?: string;
  caption?: string;
  maxWidth?: number;
}) {
  return (
    <figure className="my-4 text-center">
      <img
        src={src}
        alt={alt ?? ""}
        loading="lazy"
        className="mx-auto block h-auto w-full rounded-xl border border-stone-200/70 bg-stone-50/50"
        style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
      />
      {caption && (
        <figcaption className="mt-2 text-xs italic text-stone-500">
          {renderRich(caption)}
        </figcaption>
      )}
    </figure>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// KeyValue — label/value pairs in a tidy two-column grid.
// ──────────────────────────────────────────────────────────────────────────
export function KeyValue({
  items,
}: {
  items: { label: string; value: string }[];
}) {
  return (
    <dl className="my-2 grid grid-cols-1 gap-x-6 gap-y-2 text-[14px] sm:grid-cols-2">
      {(items || []).map((it, i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-stone-500">
            {it.label}
          </dt>
          <dd className="text-stone-800">{renderRich(it.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// DocLink — card-style link to another KB page or external doc.
// ──────────────────────────────────────────────────────────────────────────
export function DocLink({
  href,
  label,
  description,
}: {
  href: string;
  label: string;
  description?: string;
}) {
  const isExternal = /^https?:\/\//.test(href);
  const cls =
    "group my-2 flex items-start justify-between gap-3 rounded-xl border border-stone-200/70 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition hover:border-amber-300/70 hover:bg-amber-50/30";
  const body = (
    <>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-stone-900">{label}</div>
        {description && (
          <div className="mt-0.5 text-[13px] text-stone-500">
            {renderRich(description)}
          </div>
        )}
      </div>
      {isExternal ? (
        <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-stone-400 group-hover:text-amber-600" />
      ) : (
        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-stone-400 group-hover:text-amber-600" />
      )}
    </>
  );
  return isExternal ? (
    <a href={href} target="_blank" rel="noreferrer" className={cls}>
      {body}
    </a>
  ) : (
    <Link to={href} className={cls}>
      {body}
    </Link>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Chip — tiny pill.
// ──────────────────────────────────────────────────────────────────────────
export function Chip({ label, tone = "default" }: { label: string; tone?: Tone }) {
  const t = TONE[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${t.border} ${t.surface} ${t.ink}`}
    >
      {label}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Kpi — Apple-Health-style KPI strip (mirrors KpiCardsSection styling).
// ──────────────────────────────────────────────────────────────────────────
function valueClass(v: string) {
  const len = v.length;
  if (len <= 3) return "text-xl sm:text-2xl";
  if (len <= 5) return "text-lg sm:text-xl";
  return "text-base sm:text-lg";
}

export function Kpi({
  items,
}: {
  items: { label: string; value: string; trend?: "up" | "down" | "flat" }[];
}) {
  return (
    <div className="my-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {(items || []).map((item, i) => {
        const v = String(item.value);
        return (
          <div
            key={i}
            className="flex flex-col rounded-xl border border-stone-200/70 bg-white px-4 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
          >
            <p className="min-h-[2.2em] text-[10px] font-semibold uppercase leading-snug tracking-[0.18em] text-stone-500">
              {item.label}
            </p>
            <p
              dir="ltr"
              className={`mt-2 whitespace-nowrap font-semibold leading-none tracking-tight tabular-nums text-stone-900 ${valueClass(v)}`}
            >
              {v}
            </p>
            {item.trend && (
              <p
                className={
                  "mt-2 text-xs font-medium " +
                  (item.trend === "up"
                    ? "text-emerald-600"
                    : item.trend === "down"
                      ? "text-rose-600"
                      : "text-stone-500")
                }
              >
                {item.trend === "up" ? "↑" : item.trend === "down" ? "↓" : "→"}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Timeline — ordered events, each with a date dot + title + optional body.
// ──────────────────────────────────────────────────────────────────────────
export function Timeline({
  items,
}: {
  items: { date: string; title: string; body?: string }[];
}) {
  return (
    <ol className="relative my-2 space-y-5 border-s border-stone-200 ps-6">
      {(items || []).map((item, i) => (
        <li key={i} className="relative">
          <span className="absolute -start-[1.6rem] top-1.5 h-2 w-2 rounded-full bg-emerald-500" />
          <div className="text-xs font-medium text-stone-500">{item.date}</div>
          <div className="mt-0.5 text-sm font-semibold text-stone-900">
            {item.title}
          </div>
          {item.body && (
            <div className="mt-1 whitespace-pre-wrap text-sm text-stone-600">
              {renderRich(item.body)}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
